import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {catchError, filter, map, mergeMap, withLatestFrom} from 'rxjs/operators';
import { AuthState } from 'src/app/auth/state/reducer';
import { getUser } from 'src/app/auth/state/selectors';
import { Notification } from 'src/app/models/Notification';
import { Noop } from 'src/app/state/actions';
import { AppState } from 'src/app/state/reducers';
import { NotificationService } from '../notification.service';
import * as NotificationActions from './actions';
import { getNotificationShowAll } from './selectors';

@Injectable()
export class NotificationEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private notificationService: NotificationService
  ) {}

  getNotifications$ = createEffect(() => this.actions$.pipe(
    ofType(NotificationActions.GetNotifications),
    withLatestFrom(this.store.select(getUser)),
    mergeMap(([action, user]) => {
      if (user) {
        return this.notificationService.getNotifications(Number(user.id), action.page, action.perPage, action.unreadOnly).pipe(
          map((result: {notifications: Notification[], count: number}) => {
            return NotificationActions.GetNotificationsSuccess({notifications: result.notifications, totalCount: result.count, replace: !!(action.page === 0)});
          }),
          catchError((e) => {
            throw new Error(e);
          })
        );
      }

      return of(Noop());
    }))
  );

  markAsRead$ = createEffect(() => this.actions$.pipe(
    ofType(NotificationActions.MarkNotificationAsRead),
    withLatestFrom(this.store.select(getNotificationShowAll)),
    mergeMap(([action, showAll]) => {
      return this.notificationService.markAsRead(action.notification.id).pipe(
        map(success => {
          return  NotificationActions.GetNotifications({page: 0, perPage: 20, unreadOnly: !showAll})
         }))
    })), {dispatch: true});

  markAsUnread$ = createEffect(() => this.actions$.pipe(
    ofType(NotificationActions.MarkNotificationAsUnread),
    withLatestFrom(this.store.select(getNotificationShowAll)),
    mergeMap(([action, showAll]) => {
      return this.notificationService.markAsUnread(action.notification.id).pipe(
        map(success => {
          return  NotificationActions.GetNotifications({page: 0, perPage: 20, unreadOnly: !showAll})
         }))
    })), {dispatch: true});

  markAllAsRead$ = createEffect(() => this.actions$.pipe(
    ofType(NotificationActions.MarkAllNotificationsAsRead),
    withLatestFrom(this.store.select(getUser), this.store.select(getNotificationShowAll)),
    mergeMap(([action, user, showAll]) => {
      if (user) {
        return this.notificationService.markAllAsRead(Number(user.id)).pipe(
          map(success => {
           return  NotificationActions.GetNotifications({page: 0, perPage: 20, unreadOnly: !showAll})
          }))
      }
      return of(Noop());
    })), {dispatch: true});

  toggleShowAll$ = createEffect(() => this.actions$.pipe(
    ofType(NotificationActions.ToggleToShowAllNotifications),
    withLatestFrom(this.store.select(getNotificationShowAll)),
    map(([action, showAll]) => {
      // After toggle action completes, fetch notifications with the new showAll state
      return NotificationActions.GetNotifications({page: 0, perPage: 20, unreadOnly: !showAll});
    })
  ));
}
